import React, { useState, useEffect } from 'react';
import { FiX as X, FiSave as Save, FiCode as Code, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { AssertionSelector } from '../../../components/evaluations';
import HelpTooltip from '../../../../../components/common/HelpTooltip';

// Use a compatible shape for assertion items - must match AssertionSelector's AssertionItem
interface AssertionItemCompat {
  type: string;
  config?: Record<string, unknown>;
}

interface TestCaseFormData {
  name: string;
  input_variables: Record<string, unknown>;
  expected_output: string;
  expected_metadata: Record<string, unknown>;
  tags: string[];
  difficulty: string;
  importance: string;
  display_order: number | null;
  assertions: AssertionItemCompat[];
}

interface JsonErrors {
  input: string | null;
  metadata: string | null;
}

interface TestCaseData {
  name?: string;
  input_variables?: Record<string, unknown>;
  expected_output?: string;
  expected_metadata?: Record<string, unknown>;
  tags?: string[];
  difficulty?: string;
  importance?: string;
  display_order?: number | null;
  assertions_json?: string | AssertionItemCompat[];
}

interface SavePayload extends Omit<TestCaseFormData, 'assertions'> {
  assertions_json: AssertionItemCompat[] | null;
}

interface TestCaseEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (payload: SavePayload) => void;
  testCase?: TestCaseData | null;
  saving?: boolean;
}

const TestCaseEditor: React.FC<TestCaseEditorProps> = ({
  isOpen,
  onClose,
  onSave,
  testCase = null,
  saving = false,
}) => {
  const [formData, setFormData] = useState<TestCaseFormData>({
    name: '',
    input_variables: {},
    expected_output: '',
    expected_metadata: {},
    tags: [],
    difficulty: 'medium',
    importance: 'medium',
    display_order: null,
    assertions: [],
  });

  const [inputVariablesJson, setInputVariablesJson] = useState('{}');
  const [expectedMetadataJson, setExpectedMetadataJson] = useState('{}');
  const [tagsInput, setTagsInput] = useState('');
  const [jsonError, setJsonError] = useState<JsonErrors>({ input: null, metadata: null });
  const [showAssertions, setShowAssertions] = useState(false);

  useEffect(() => {
    if (testCase) {
      let assertions: AssertionItemCompat[] = [];
      if (testCase.assertions_json) {
        try {
          assertions =
            typeof testCase.assertions_json === 'string'
              ? (JSON.parse(testCase.assertions_json) as AssertionItemCompat[])
              : testCase.assertions_json;
        } catch {
          assertions = [];
        }
      }

      setFormData({
        name: testCase.name || '',
        input_variables: testCase.input_variables || {},
        expected_output: testCase.expected_output || '',
        expected_metadata: testCase.expected_metadata || {},
        tags: testCase.tags || [],
        difficulty: testCase.difficulty || 'medium',
        importance: testCase.importance || 'medium',
        display_order: testCase.display_order ?? null,
        assertions,
      });
      setInputVariablesJson(JSON.stringify(testCase.input_variables || {}, null, 2));
      setExpectedMetadataJson(JSON.stringify(testCase.expected_metadata || {}, null, 2));
      setTagsInput((testCase.tags || []).join(', '));
      setShowAssertions(assertions.length > 0);
    } else {
      setFormData({
        name: '',
        input_variables: {},
        expected_output: '',
        expected_metadata: {},
        tags: [],
        difficulty: 'medium',
        importance: 'medium',
        display_order: null,
        assertions: [],
      });
      setInputVariablesJson('{}');
      setExpectedMetadataJson('{}');
      setTagsInput('');
      setShowAssertions(false);
    }
    setJsonError({ input: null, metadata: null });
  }, [testCase, isOpen]);

  const handleJsonChange = (value: string, field: 'input' | 'metadata') => {
    if (field === 'input') {
      setInputVariablesJson(value);
    } else {
      setExpectedMetadataJson(value);
    }

    try {
      const parsed = JSON.parse(value) as Record<string, unknown>;
      setJsonError((prev) => ({ ...prev, [field]: null }));
      setFormData((prev) => ({
        ...prev,
        [field === 'input' ? 'input_variables' : 'expected_metadata']: parsed,
      }));
    } catch {
      setJsonError((prev) => ({ ...prev, [field]: 'Invalid JSON format' }));
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (jsonError.input || jsonError.metadata) {
      return;
    }

    const tags = tagsInput?.split(',').map((t) => t.trim()).filter((t) => t) ?? [];
    const assertions_json = formData.assertions.length > 0 ? formData.assertions : null;

    onSave({ ...formData, tags, assertions_json });
  };

  if (!isOpen) return null;

  let submitButtonLabel: string;
  if (saving) {
    submitButtonLabel = 'Saving...';
  } else if (testCase) {
    submitButtonLabel = 'Update';
  } else {
    submitButtonLabel = 'Create';
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {testCase ? 'Edit Test Case' : 'Add Test Case'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name *
              </label>
              <input id="name"
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 transition-colors"
                placeholder="e.g., Basic greeting response"
              />
            </div>

            {/* Input Variables (JSON) */}
            <div>
              <label htmlFor="input-variables-json" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Code className="w-4 h-4 inline mr-1" />
                Input Variables (JSON)
                <HelpTooltip text="The data that will be sent to the LLM during evaluation. Use JSON key-value pairs where keys match your prompt template variables (e.g. {{query}})." />
              </label>
              <textarea id="input-variables-json"
                value={inputVariablesJson}
                onChange={(e) => handleJsonChange(e.target.value, 'input')}
                className={`w-full px-3 py-2 border rounded-lg font-mono text-sm
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 transition-colors
                         ${jsonError.input ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                rows={6}
                placeholder='{"variable1": "value1", "variable2": "value2"}'
              />
              {jsonError.input && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">{jsonError.input}</p>
              )}
            </div>

            {/* Expected Output */}
            <div>
              <label htmlFor="expected-output" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Expected Output *
                <HelpTooltip text="The ideal response the LLM should produce. Used by scoring methods to measure how close the actual output is to this expected answer." />
              </label>
              <textarea id="expected-output"
                required
                value={formData.expected_output}
                onChange={(e) => setFormData({ ...formData, expected_output: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 transition-colors"
                rows={4}
                placeholder="The expected response or output"
              />
            </div>

            {/* Expected Metadata (JSON) */}
            <div>
              <label htmlFor="expected-metadata-json" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Code className="w-4 h-4 inline mr-1" />
                Expected Metadata (JSON, optional)
                <HelpTooltip text="Additional expected data beyond the main output, such as tone, format, or specific facts. Used by advanced scoring methods." />
              </label>
              <textarea id="expected-metadata-json"
                value={expectedMetadataJson}
                onChange={(e) => handleJsonChange(e.target.value, 'metadata')}
                className={`w-full px-3 py-2 border rounded-lg font-mono text-sm
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 transition-colors
                         ${jsonError.metadata ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                rows={4}
                placeholder='{"sentiment": "positive", "category": "greeting"}'
              />
              {jsonError.metadata && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">{jsonError.metadata}</p>
              )}
            </div>

            {/* Difficulty & Importance Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="difficulty" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Difficulty
                </label>
                <select id="difficulty"
                  value={formData.difficulty}
                  onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                           focus:ring-2 focus:ring-blue-500 transition-colors"
                >
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>
              <div>
                <label htmlFor="importance" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Importance
                </label>
                <select id="importance"
                  value={formData.importance}
                  onChange={(e) => setFormData({ ...formData, importance: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                           focus:ring-2 focus:ring-blue-500 transition-colors"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>

            {/* Tags */}
            <div>
              <label htmlFor="tags-comma-separated" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Tags (comma-separated)
              </label>
              <input id="tags-comma-separated"
                type="text"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                         focus:ring-2 focus:ring-blue-500 transition-colors"
                placeholder="e.g., greeting, basic, english"
              />
            </div>

            {/* Assertions Section */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => setShowAssertions(!showAssertions)}
                className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 dark:text-white">Assertions</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    ({formData.assertions.length} configured)
                  </span>
                </div>
                {showAssertions ? (
                  <FiChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <FiChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>

              {showAssertions && (
                <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    Add assertions to automatically validate test case outputs during evaluation.
                  </p>
                  <AssertionSelector
                    assertions={formData.assertions}
                    onChange={(assertions) => setFormData({ ...formData, assertions })}
                    disabled={saving}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                       text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700
                       transition-colors"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !!jsonError.input || !!jsonError.metadata}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg
                       hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed
                       transition-colors"
            >
              <Save className="w-4 h-4" />
              {submitButtonLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TestCaseEditor;
